import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { useT } from '../app/preferences.ts';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Render custom content without the default header. */
  bare?: boolean;
  /** Extra class on the <dialog> for screens with their own look. */
  className?: string;
}

/** Native <dialog>: focus trapping, Esc and backdrop come for free. */
export function Dialog({ open, onClose, title, children, footer, bare, className }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const t = useT();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={className ? `dialog ${className}` : 'dialog'}
      aria-label={title}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose(); // backdrop click
      }}
    >
      {open && (
        <>
          {!bare && title && (
            <div className="dialog-header">
              <h2 className="dialog-title">{title}</h2>
              <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
                <X size={28} strokeWidth={2.5} />
              </button>
            </div>
          )}
          {children}
          {footer && <div className="dialog-footer">{footer}</div>}
        </>
      )}
    </dialog>
  );
}

interface ConfirmProps {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  /** Defaults to "keep playing" (the in-game wording). */
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({ open, title, body, confirmLabel, cancelLabel, danger, onConfirm, onCancel }: ConfirmProps) {
  const t = useT();
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          {/* The safe choice is the big friendly default. */}
          <button type="button" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
          <button type="button" className="btn btn-green" onClick={onCancel} autoFocus>
            {cancelLabel ?? t('common.cancel')}
          </button>
        </>
      }
    >
      <div className="dialog-body">{body}</div>
    </Dialog>
  );
}
